import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest } from "../../../core/common/api";
import Tree from "../../../shared/Tree";
import ResetButton from "../../../shared/ResetButton";
import { MATRIX_SEQUENCE, clubLabel } from "../../../shared/Utils";

const NetworkTree = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [memberId, setMemberId] = useState(searchParams.get("memberId") || "");
  const [tree, setTree] = useState<any>(null);
  const [total, setTotal] = useState(0);
  const [club, setClub] = useState(searchParams.get("club") || "all");
  const [matched, setMatched] = useState(0);
  const [clubs, setClubs] = useState<Array<{ club: number; name: string; count: number }>>([]);
  const requestId = useRef(0);
  const [isLoading, setIsLoading] = useState(false);

  const loadTree = useCallback(
    async (code = "", selectedClub = "all") => {
      const currentRequest = ++requestId.current;
      setIsLoading(true);
      try {
        const query = new URLSearchParams();
        if (code.trim()) query.set("memberId", code.trim());
        if (selectedClub !== "all") query.set("club", selectedClub);
        const res = await apiRequest(`/sale-users/tree?${query}`, "GET");
        if (currentRequest !== requestId.current) return;
        if (!res.success || !res.tree) {
          throw new Error(res.message || "Tree not found");
        }
        setTree(res.tree);
        setTotal(res.total || 0);
        setMatched(res.matched ?? res.total ?? 0);
        setClubs(res.clubs || []);
        setClub(res.club == null ? "all" : String(res.club));
        if (res.tree.memberId) {
          setMemberId(res.tree.memberId);
          const params: Record<string, string> = { memberId: res.tree.memberId };
          if (res.club != null) params.club = String(res.club);
          setSearchParams(params, { replace: true });
        }
      } catch (error: any) {
        if (currentRequest !== requestId.current) return;
        setTree(null);
        setMatched(0);
        setTotal(0);
        Swal.fire("Error", error.message || "Unable to load tree", "error");
      } finally {
        if (currentRequest === requestId.current) setIsLoading(false);
      }
    },
    [setSearchParams],
  );

  useEffect(() => {
    void loadTree(searchParams.get("memberId") || "", searchParams.get("club") || "all");
    return () => { requestId.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    void loadTree(memberId, club);
  };

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">User Management</div>
          <h1 className="page-title">
            Network <em>Tree</em>
          </h1>
          <div className="page-sub">
            Sale-network placement tree (saleusermodel). Search any sale member ID
            to inspect their 3-leg downline.
          </div>
        </div>
      </div>

      <form className="toolbar" onSubmit={handleSearch}>
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={memberId}
            onChange={(event) => setMemberId(event.target.value.toUpperCase())}
            placeholder="Enter member ID (RW960001)"
          />
        </div>
        <select className="filter-select" aria-label="Club filter" value={club} disabled={isLoading}
          onChange={event => { setClub(event.target.value); void loadTree(memberId, event.target.value); }}>
          <option value="all">All clubs</option>
          {(clubs.length ? clubs : MATRIX_SEQUENCE.map(value => ({ club: value, name: clubLabel(value), count: 0 }))).map(item =>
            <option key={item.club} value={String(item.club)}>{item.name}{clubs.length ? ` (${item.count})` : ""}</option>
          )}
        </select>
        <button
          type="submit"
          className="btn-sm btn-outline"
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Show Tree"}
        </button>
        <ResetButton
          onClick={() => {
            setMemberId("");
            setClub("all");
            setSearchParams({}, { replace: true });
            void loadTree("", "all");
          }}
        />
        {tree?._id && (
          <Link to={`/sale-users/${tree._id}`} className="btn-sm btn-outline">
            View Profile
          </Link>
        )}
      </form>

      <section className="table-card mlm-tree-panel">
        <div className="table-head">
          <div>
            <h2>
              {tree?.memberId
                ? `${tree.memberId} · ${tree.name || "Member"}`
                : "Company Tree"}
            </h2>
            <p>
              {isLoading
                ? "Loading downline..."
                : club === "all" ? `${total} member${total === 1 ? "" : "s"} in this tree`
                  : `${matched} ${clubLabel(club)} member${matched === 1 ? "" : "s"} matched · ${total} total. Placement ancestors remain visible.`}
            </p>
          </div>
        </div>
        {isLoading ? (
          <div className="empty-state">Loading tree...</div>
        ) : tree ? (
          <Tree node={tree} defaultExpandDepth={2} filtered={club !== "all"} />
        ) : (
          <div className="empty-state">No tree found for this member ID.</div>
        )}
      </section>
    </>
  );
};

export default NetworkTree;
